import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import {
  ArrowRight,
  BadgePercent,
  Clock,
  Eye,
  FileText,
  Landmark,
  Lightbulb,
  LockKeyhole,
  MapPin,
  MessagesSquare,
  Mountain,
  Receipt,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  UtensilsCrossed,
} from 'lucide-react';
import { Avatar, Container, Glow, Section, SectionHeader } from './ui';
import { Stagger, fadeUp } from './motion';
import SmartLink from './SmartLink';

const Tile = ({ className, children, glow }) => (
  <motion.article
    variants={fadeUp}
    whileHover={{ y: -4 }}
    transition={{ type: 'spring', stiffness: 300, damping: 26 }}
    className={clsx(
      'surface group flex flex-col p-6 transition-colors duration-300 hover:border-white/[0.16] sm:p-8',
      className
    )}
  >
    {glow && <Glow className={clsx('h-64 w-64 opacity-0 transition-opacity duration-500 group-hover:opacity-100', glow)} />}
    <div className="relative flex h-full flex-col">{children}</div>
  </motion.article>
);

const TileHeading = ({ icon: Icon, label, title, children }) => (
  <div>
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-brand-400/20 bg-brand-400/10 text-brand-300">
        <Icon className="h-5 w-5" />
      </span>
      {label && <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</span>}
    </div>
    <h3 className="mt-6 text-2xl font-bold tracking-tight text-white sm:text-3xl">{title}</h3>
    {children && <p className="mt-3 leading-relaxed text-slate-400">{children}</p>}
  </div>
);

/* ---- Trust: Peace of Mind / Resolution Center ---- */
const ResolutionPreview = () => (
  <div className="surface-inset mt-8 flex-1 p-4 sm:p-5">
    <div className="mb-4 flex items-center justify-between">
      <span className="flex items-center gap-2 text-sm font-semibold text-white">
        <MessagesSquare className="h-4 w-4 text-brand-300" />
        Resolution Center
      </span>
      <span className="rounded-full bg-amber-400/10 px-2.5 py-0.5 text-xs font-medium text-amber-300">Open</span>
    </div>
    <div className="space-y-3">
      <div className="flex gap-3">
        <Avatar initials="JD" color="green" />
        <div className="flex-1 rounded-2xl rounded-tl-sm bg-white/[0.04] p-3.5">
          <div className="flex justify-between text-sm">
            <span className="font-medium text-white">John Davis</span>
            <span className="text-slate-500">2h ago</span>
          </div>
          <p className="mt-1 text-sm text-slate-300">I believe we agreed on a 60/40 split for this expense.</p>
        </div>
      </div>
      <div className="flex gap-3">
        <Avatar initials="SM" color="purple" />
        <div className="flex-1 rounded-2xl rounded-tl-sm bg-white/[0.04] p-3.5">
          <div className="flex justify-between text-sm">
            <span className="font-medium text-white">Sarah Miller</span>
            <span className="text-slate-500">1h ago</span>
          </div>
          <p className="mt-1 text-sm text-slate-300">I thought we agreed on 50/50. Can we review the original agreement?</p>
        </div>
      </div>
      <div className="rounded-2xl border border-brand-400/25 bg-brand-400/[0.07] p-3.5">
        <div className="flex items-center gap-2 text-sm font-medium text-white">
          <Sparkles className="h-4 w-4 text-brand-300" />
          Resolution Assistant
        </div>
        <p className="mt-1.5 text-sm text-slate-300">
          I've found the original agreement from June 10. It states a 60/40 split for this category of expenses.
        </p>
      </div>
      <div className="flex gap-2 pt-1">
        <button type="button" className="flex-1 cursor-pointer rounded-xl bg-brand-400 py-2.5 text-sm font-semibold text-ink-950 transition-colors hover:bg-brand-300">
          Accept
        </button>
        <button type="button" className="flex-1 cursor-pointer rounded-xl border border-white/10 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:border-white/25">
          Request review
        </button>
      </div>
    </div>
  </div>
);

/* ---- Transparency: live ledger ---- */
const LEDGER = [
  { initials: 'JD', color: 'blue', amount: '+$350.00' },
  { initials: 'KS', color: 'green', amount: '+$350.00' },
  { initials: 'MR', color: 'amber', amount: '+$350.00' },
];

const LedgerPreview = () => (
  <div className="surface-inset mt-6 space-y-2 p-3">
    {LEDGER.map((row, i) => (
      <motion.div
        key={row.initials}
        initial={{ opacity: 0, x: -12 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 + i * 0.15, duration: 0.5 }}
        className="flex items-center justify-between rounded-xl bg-white/[0.03] px-3 py-2"
      >
        <span className="flex items-center gap-2.5">
          <Avatar initials={row.initials} color={row.color} size="sm" />
          <span className="text-sm text-slate-300">Contribute</span>
        </span>
        <span className="text-sm font-semibold tabular-nums text-emerald-300">{row.amount}</span>
      </motion.div>
    ))}
  </div>
);

/* ---- Control: approval rules ---- */
const ControlPreview = () => (
  <div className="surface-inset mt-6 space-y-2 p-3">
    {['Majority', 'Unanimous'].map((rule, i) => (
      <div key={rule} className="flex items-center justify-between rounded-xl bg-white/[0.03] px-3 py-2.5">
        <span className="text-sm text-slate-300">{rule}</span>
        <span
          className={clsx(
            'relative h-6 w-11 rounded-full transition-colors',
            i === 0 ? 'bg-brand-400' : 'bg-white/10'
          )}
        >
          <span
            className={clsx(
              'absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-all',
              i === 0 ? 'left-6' : 'left-1'
            )}
          />
        </span>
      </div>
    ))}
  </div>
);

/* ---- Save Money: discounts ---- */
const DISCOUNTS = [
  { icon: UtensilsCrossed, category: 'Restaurant', off: '20% OFF', name: 'Skyline Bistro', detail: 'Groups of 6+ get 20% discount on total bill', meta: '1.2 miles away', metaIcon: MapPin },
  { icon: Landmark, category: 'Travel', off: '15% OFF', name: 'City Hotel', detail: '15% off when booking 3+ rooms', meta: 'Valid until Dec 31', metaIcon: Clock },
];

const DiscountPreview = () => (
  <div className="mt-6 grid gap-3">
    {DISCOUNTS.map((d) => (
      <div key={d.name} className="surface-inset p-4 transition-colors duration-300 hover:border-brand-400/30">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-300">
            <d.icon className="h-3.5 w-3.5" />
            {d.category}
          </span>
          <span className="rounded-full bg-emerald-400/10 px-2 py-0.5 text-xs font-bold text-emerald-300">{d.off}</span>
        </div>
        <p className="mt-2 font-semibold text-white">{d.name}</p>
        <p className="mt-0.5 text-sm text-slate-400">{d.detail}</p>
        <p className="mt-2 flex items-center gap-1 text-xs text-slate-500">
          <d.metaIcon className="h-3.5 w-3.5" />
          {d.meta}
        </p>
      </div>
    ))}
  </div>
);

/* ---- Stay Focused: AI suggestions ---- */
const SUGGESTIONS = [
  { title: 'National Park Camping Trip', reason: "Combines your group's interest in nature and outdoor activities.", match: 98 },
  { title: 'Historic City Tour', reason: "Aligns with your group's interest in cultural experiences.", match: 95 },
  { title: 'Food Festival Weekend', reason: 'New experience your group might enjoy based on preferences.', match: 82 },
];

const SuggestionPreview = () => (
  <div className="mt-8 grid items-start gap-4 md:grid-cols-5">
    <div className="surface-inset p-5 md:col-span-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">AI Suggestion Engine</p>
      <h4 className="mt-3 font-semibold text-white">Your Group Profile</h4>
      <p className="mt-2 text-sm leading-relaxed text-slate-400">
        Family trip fund with 6 members. Previous activities: hiking, beach vacations, and cultural events.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {['Family', 'Travel', 'Outdoors', 'Culture'].map((tag) => (
          <span key={tag} className="rounded-full border border-brand-400/20 bg-brand-400/10 px-2.5 py-0.5 text-xs text-brand-300">
            {tag}
          </span>
        ))}
      </div>
    </div>
    <div className="space-y-2.5 md:col-span-3">
      <p className="text-sm font-medium text-white">Suggested Activities for Your Group:</p>
      {SUGGESTIONS.map((s, i) => (
        <div key={s.title} className="surface-inset flex items-start gap-3 p-3.5 transition-colors duration-300 hover:border-brand-400/30">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-400/10 text-emerald-300">
            {i === 0 ? <Mountain className="h-4 w-4" /> : <Lightbulb className="h-4 w-4" />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-white">{s.title}</p>
              <span className="shrink-0 rounded-full bg-emerald-400/10 px-2 py-0.5 text-xs font-semibold text-emerald-300">
                {s.match}% Match
              </span>
            </div>
            <p className="mt-0.5 text-xs leading-relaxed text-slate-400">{s.reason}</p>
          </div>
        </div>
      ))}
      <SmartLink to="/extra-benefits" className="link-arrow pt-1">
        <Sparkles className="h-4 w-4" />
        Generate more suggestions
      </SmartLink>
    </div>
  </div>
);

const FeaturesBento = () => (
  <Section id="features">
    <Glow className="left-[-10rem] top-40 h-96 w-96 bg-brand-500/10" />
    <Container className="relative">
      <SectionHeader
        eyebrow="Powerful Features"
        title="Why Choose Groupmoney"
        description="Our platform offers unique features that make group finances simple, transparent, and stress-free."
      />

      <Stagger className="mt-16 grid auto-rows-auto grid-cols-1 gap-4 sm:mt-20 md:grid-cols-6 lg:gap-5" stagger={0.1}>
        {/* Trust — large hero tile */}
        <Tile className="md:col-span-6 lg:col-span-4 lg:row-span-2" glow="-right-20 -top-20 bg-brand-500/20">
          <TileHeading icon={ShieldCheck} label="Trust" title="Peace of Mind">
            Resolution Center helps solve conflicts and Continuity Directive protects your funds, ensuring everyone in your group feels secure and protected.
          </TileHeading>
          <div className="grid flex-1 gap-6 lg:grid-cols-2">
            <div className="flex flex-col">
              <div className="mt-8 space-y-3">
                {[
                  { icon: MessagesSquare, title: 'Resolution Center', text: 'Conflict resolution with built-in mediation tools' },
                  { icon: LockKeyhole, title: 'Continuity Directive', text: 'Continuity planning for long-term group financial security' },
                ].map(({ icon: Icon, title, text }) => (
                  <div key={title} className="surface-inset flex gap-4 p-5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-400/10 text-brand-300">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-semibold text-white">{title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-slate-400">{text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <SmartLink to="/resolution-center" className="link-arrow mt-6 lg:mt-auto">
                Learn more about conflict resolution
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </SmartLink>
            </div>
            <ResolutionPreview />
          </div>
        </Tile>

        {/* Transparency */}
        <Tile className="md:col-span-3 lg:col-span-2" glow="-right-24 -top-24 bg-emerald-500/20">
          <TileHeading icon={Eye} label="Transparency" title="Transparency">
            Transparency builds confidence among group members with visible activity for all.
          </TileHeading>
          <LedgerPreview />
        </Tile>

        {/* Control */}
        <Tile className="md:col-span-3 lg:col-span-2" glow="-right-24 -top-24 bg-violet-500/20">
          <TileHeading icon={SlidersHorizontal} label="Control" title="Group Controls">
            Customizable approval workflows
          </TileHeading>
          <ControlPreview />
          <SmartLink to="/group-controls" className="link-arrow mt-5">
            Spend the money with clear approvals
            <ArrowRight className="h-4 w-4" />
          </SmartLink>
        </Tile>

        {/* Save Money */}
        <Tile className="md:col-span-6 lg:col-span-2" glow="-left-24 -bottom-24 bg-emerald-500/15">
          <TileHeading icon={BadgePercent} title="Save Money">
            Unlock group discounts on restaurants, trips, and hotels by doing things together. Our platform helps you save more through collective purchasing power.
          </TileHeading>
          <DiscountPreview />
          <SmartLink to="/group-discounts" className="link-arrow mt-5">
            View all 46 discounts
            <ArrowRight className="h-4 w-4" />
          </SmartLink>
        </Tile>

        {/* Stay Focused */}
        <Tile className="md:col-span-6 lg:col-span-4" glow="-right-20 -bottom-20 bg-blue-500/20">
          <TileHeading icon={Sparkles} title="Stay Focused">
            AI-powered Idea Suggestions recommend activities or goals for your group, helping everyone stay aligned on what matters most.
          </TileHeading>
          <SuggestionPreview />
        </Tile>

        {/* Documentation / all features banner */}
        <Tile className="md:col-span-6" glow="left-1/3 -bottom-32 bg-brand-500/15">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-5">
              <span className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-brand-400/20 bg-brand-400/10 text-brand-300 sm:flex">
                <FileText className="h-6 w-6" />
              </span>
              <div>
                <h3 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">Discover All Groupmoney Features</h3>
                <p className="mt-2 max-w-2xl leading-relaxed text-slate-400">
                  Transparency, control, documentation, and more. Explore the full range of features designed to make group finances simple.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {[
                    { icon: Receipt, label: 'Receipt uploading and tracking' },
                    { icon: Eye, label: 'Real-time contribution tracking' },
                  ].map(({ icon: Icon, label }) => (
                    <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-slate-300">
                      <Icon className="h-3.5 w-3.5 text-brand-300" />
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <a href="#how-it-works" className="btn-secondary shrink-0">
              View All Features
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </Tile>
      </Stagger>
    </Container>
  </Section>
);

export default FeaturesBento;
